import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
// import { findIndex, unionWith } from 'ramda';
import { Diagnostic } from 'vscode-languageserver-protocol';
import { DEFAULT_PATH_PREVIEW_MODE, Path, PathPreviewMode, Waypoint } from '../Components/PathBuilder/pathBuilderTypes';
import { CollaboratorInfo, Project } from '../Components/Projects/projects';
import type { BuildSubTab } from '../utils/buildSubTabs';
import { DefaultSettings, RobotActions } from '../utils/constants';
import { getFieldGeometry, normalizeFieldSelection } from '../utils/fieldSelection';
import { rotateWaypoint90InField } from '../utils/path-builder/path-builder-helpers';
import {
  clampSegmentSpeed,
  migratePath,
  migrateProjectPaths,
  syncSegmentSpeedsAfterWaypointEdit,
  syncSegmentSpeedsLen,
} from '../utils/path-builder/path-segment-speeds';
import { getValidSaveSlotNumber } from '../utils/saveSlotProjectId';
import { applyProjectVersionDefaults, type ProjectVersionFields } from '../utils/projectVersions';
import { robotSlice } from './RobotSlice';
import type { RootState } from './Store';

/** Last user-selected run mode: "download" | "run" | "download_run" - used for deploy-only vs deploy+run toasts */
export type RunMode = 'download' | 'run' | 'download_run';
const MAX_CONSOLE_DISPLAY_LINES = 10000;
const CONSOLE_TRIM_BATCH = 100;

/** Strip trailing line breaks so each `consoleDisplay` row is one logical line. */
function normalizeConsoleDisplayLine(text: unknown): string {
  return String(text ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\n+$/g, '');
}
const DEFAULT_EDITOR_DRAWER_TAB = 'config-configure';

/** Serializable error format - avoid storing Error objects in Redux */
export interface ProgramError {
  code: number;
  message: string | string[];
}

export type SetDiagnosticsPayload = Diagnostic[] | { diagnostics: Diagnostic[]; programCode: string };

export interface PendingProgramReplacement {
  code: string;
  projectId: string;
  lastModified?: string;
}

interface ProjectSlice {
  error: ProgramError | null;
  sensorData: boolean;
  programState: string;
  /** Persists run mode when DOWNLOAD/DOWNLOAD_RUN/RUN is dispatched, for program state notifications */
  lastRunMode: RunMode | null;
  drive: boolean;
  loadedProject: Project | null;
  projects: Array<Project>;
  sortOrder: string;
  projectsLoading: boolean;
  projectSelect: {
    isMultiSelect: boolean;
    selectedProjects: Array<Project>;
  };
  copiedProject: string;
  lazyLoading: boolean;
  diagnostics: Array<Diagnostic>;
  /** Loaded project id that produced the current diagnostics snapshot. */
  diagnosticsProjectId: string | null;
  /** Program code that produced the current diagnostics snapshot. */
  diagnosticsProgramCode: string | null;
  /** True while Pyright is initializing or publishing diagnostics for the loaded program. */
  linterDiagnosticsPending: boolean;
  dashboardOpenDrawer: boolean;
  pathBuilderOpenDrawer: boolean;
  bottomBarOpenDrawer: boolean;
  consoleDisplay: string[];
  pendingProgramReplacement: PendingProgramReplacement | null;
  leftDrawerWidths?: {
    codeEditor?: number;
    dashboard?: number;
    pathBuilder?: number;
  };
  // Persisted bottom drawer content height in pixels
  bottomDrawerHeight?: number;
  // Which drawer is currently open (UI state, not tied to a specific project)
  openDrawer: string | null;
  /** Deploy save slot for the current project session (0 = unset). Not persisted in project files. */
  sessionSaveSlot: number;
  /** Concrete slot captured when a deploy/run action is queued; used until that action completes. */
  activeProgramActionSlotNumber: number | null;
  /** Robot-authoritative running program label for snackbars (e.g. DU-started runs). */
  activeRunningProgramDisplayName: string | null;
  /** Robot-authoritative running program slot for snackbars (1–8). */
  activeRunningProgramSlotNumber: number | null;
  /** 0–100 deploy transfer progress while programState is SEND_STARTING; null when not deploying. */
  programDeployProgress: number | null;
}

const initialState: ProjectSlice = {
  error: null,
  sensorData: DefaultSettings.sensorData,
  programState: RobotActions.PROGRAM.STOPPED,
  lastRunMode: null,
  drive: false,
  loadedProject: null,
  projects: [],
  sortOrder: 'newest',
  projectsLoading: false,
  projectSelect: {
    isMultiSelect: false,
    selectedProjects: [],
  },
  copiedProject: '',
  lazyLoading: false,
  diagnostics: [],
  diagnosticsProjectId: null,
  diagnosticsProgramCode: null,
  linterDiagnosticsPending: false,
  dashboardOpenDrawer: true,
  pathBuilderOpenDrawer: true,
  bottomBarOpenDrawer: false,
  consoleDisplay: [],
  pendingProgramReplacement: null,
  leftDrawerWidths: {},
  bottomDrawerHeight: undefined,
  openDrawer: null,
  sessionSaveSlot: 0,
  activeProgramActionSlotNumber: null,
  activeRunningProgramDisplayName: null,
  activeRunningProgramSlotNumber: null,
  programDeployProgress: null,
};

const normalizeProject = <T extends Project | Draft<Project>>(project: T): T => {
  const normalized = { ...project } as T & { type?: unknown; selectedSaveSlot?: unknown };
  delete normalized.type;
  delete normalized.selectedSaveSlot;

  return {
    ...applyProjectVersionDefaults(normalized),
    selectedField: normalizeFieldSelection(project.selectedField),
    pathPreviewMode: project.pathPreviewMode ?? DEFAULT_PATH_PREVIEW_MODE,
    commandManagementEnabled: project.commandManagementEnabled ?? true,
  } as T;
};

const hydrateLoadedProject = <T extends Project | Draft<Project>>(project: T): T => {
  const normalizedProject = normalizeProject(project);
  return {
    ...normalizedProject,
    selectedEditorDrawerTab: normalizedProject.selectedEditorDrawerTab ?? DEFAULT_EDITOR_DRAWER_TAB,
    editorOpenDrawer: normalizedProject.editorOpenDrawer !== undefined ? normalizedProject.editorOpenDrawer : true,
  } as T;
};

const projectApiMetadata = (source: ProjectVersionFields): ProjectVersionFields => ({
  ...(source.version && { version: source.version }),
  ...(source.frameworkVersion && { frameworkVersion: source.frameworkVersion }),
});

// Helper function to update loadedProject and sync to projects array
const updateLoadedProject = (state: Draft<ProjectSlice>, updater: (project: Draft<Project>) => Project | Draft<Project>) => {
  if (!state.loadedProject) return;
  const updated = normalizeProject(updater(state.loadedProject)) as Draft<Project>;
  state.loadedProject = updated;
  state.projects = state.projects.map((project) => (project.id === updated.id ? updated : project));
};

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    // TODO is this actually used?
    onUpdateSensorDataEnabled: (state, { payload }) => ({
      ...state,
      sensorData: payload,
    }),
    onUpdateProgramState: (state, { payload }) => {
      state.programState = payload.programState;
      state.error = payload.error;
      state.programDeployProgress = null;
      if (
        payload.runMode &&
        [RobotActions.PROGRAM.DOWNLOAD, RobotActions.PROGRAM.DOWNLOAD_RUN, RobotActions.PROGRAM.RUN].includes(payload.programState)
      ) {
        state.lastRunMode = payload.runMode;
        state.activeProgramActionSlotNumber = getValidSaveSlotNumber(payload.targetSlotNumber) ?? getValidSaveSlotNumber(state.sessionSaveSlot);
        state.activeRunningProgramDisplayName = null;
        state.activeRunningProgramSlotNumber = null;
      }

      if (
        [
          RobotActions.PROGRAM.SEND_SUCCEEDED,
          RobotActions.PROGRAM.SEND_FAILED,
          RobotActions.PROGRAM.ERROR,
          RobotActions.PROGRAM.STOP,
          RobotActions.PROGRAM.STOPPED,
        ].includes(payload.programState)
      ) {
        state.activeProgramActionSlotNumber = null;
      }

      if ([RobotActions.PROGRAM.SEND_FAILED, RobotActions.PROGRAM.ERROR, RobotActions.PROGRAM.STOPPED].includes(payload.programState)) {
        state.activeRunningProgramDisplayName = null;
        state.activeRunningProgramSlotNumber = null;
      }
    },
    setActiveRunningProgramNotificationContext: (state, { payload }: PayloadAction<{ displayName: string; slotNumber?: number | null }>) => {
      const displayName = payload.displayName.trim();
      if (!displayName) {
        return;
      }

      state.activeRunningProgramDisplayName = displayName;
      state.activeRunningProgramSlotNumber = getValidSaveSlotNumber(payload.slotNumber);
    },
    onUpdateProgramDeployProgress: (state, { payload }: PayloadAction<number | null>) => {
      if (state.programState !== RobotActions.PROGRAM.SEND_STARTING) {
        return;
      }
      state.programDeployProgress = payload;
    },
    onStopProgram: (state) => {
      if (state.programState === RobotActions.PROGRAM.STARTED) {
        state.programState = RobotActions.PROGRAM.STOP;
      }
    },
    onUpdateProgramDrive: (state, { payload }) => ({
      ...state,
      drive: payload,
    }),
    onAddAndUpsert: (state) => {
      state.error = null;
      state.programState = RobotActions.PROGRAM.STOP;
    },
    toggleDashboardOpenDrawer: (state) => ({ ...state, dashboardOpenDrawer: !state.dashboardOpenDrawer }),
    setDashboardOpenDrawer: (state, { payload }: PayloadAction<boolean>) => {
      state.dashboardOpenDrawer = payload;
    },
    togglePathBuilderOpenDrawer: (state) => ({ ...state, pathBuilderOpenDrawer: !state.pathBuilderOpenDrawer }),
    setPathBuilderOpenDrawer: (state, { payload }: PayloadAction<boolean>) => ({ ...state, pathBuilderOpenDrawer: payload }),
    toggleEditorOpenDrawer: (state) => {
      updateLoadedProject(state, (project) => ({
        ...project,
        editorOpenDrawer: !project.editorOpenDrawer,
      }));
    },
    onUpdateEditorDrawer: (state, { payload }) => ({ ...state, editorOpenDrawer: payload }),
    toggleBottomBarOpenDrawer: (state) => ({ ...state, bottomBarOpenDrawer: !state.bottomBarOpenDrawer }),
    onAddConsoleDisplay: (state, { payload }) => {
      if (state.consoleDisplay.length >= MAX_CONSOLE_DISPLAY_LINES) {
        state.consoleDisplay.splice(0, CONSOLE_TRIM_BATCH);
      }
      state.consoleDisplay.push(normalizeConsoleDisplayLine(payload));
    },
    onAddConsoleDisplayBatch: (state, { payload }: PayloadAction<string[]>) => {
      if (!Array.isArray(payload) || payload.length === 0) return;
      const incoming = payload.map(normalizeConsoleDisplayLine).slice(-MAX_CONSOLE_DISPLAY_LINES);
      const overflow = state.consoleDisplay.length + incoming.length - MAX_CONSOLE_DISPLAY_LINES;
      if (overflow > 0) {
        state.consoleDisplay.splice(0, Math.min(overflow, state.consoleDisplay.length));
      }
      state.consoleDisplay.push(...incoming);
    },
    onClearConsoleDisplay: (state) => ({ ...state, consoleDisplay: [] }),
    // addProgramSuceeded: (state, payload) => {
    //   const { program, copiedProgram } = payload;
    //   const _program = { ...program };
    //   const virtualMiniIndex = findIndex((robot) => +robot.id === 107, _program.robots);
    //   if (virtualMiniIndex !== -1) {
    //     _program.robots.splice(virtualMiniIndex, 1, ROBOTS.MINI);
    //   }
    //   const programs = [...state.programs];
    //   const index = findIndex((p) => p.identifier === program.identifier, programs);
    //   if (index === -1) {
    //     programs.push(_program);
    //   } else {
    //     programs.splice(index, 1, _program);
    //   }
    //   return {
    //     ...state,
    //     programs,
    //     copiedProgram,
    //   };
    // },
    addProgramFailed: (state, { payload }) => {
      // TODO figure out how to handle failed uploads here.
      console.error(payload);
      state.error = payload;
    },
    onDeleteProject: (state, { payload }) => {
      state.projects = state.projects.filter((p) => p.id !== payload.id);
    },
    onDeleteFromServer: (state, { payload }) => {
      state.projects = payload.map((project: Project) => migrateProjectPaths(project) as Project);
    },
    onSetSortOrder: (state, { payload }) => ({
      ...state,
      sortOrder: payload,
    }),
    onUpdateProjects: (state, { payload }) => {
      state.projects = payload.map((project: Project) => normalizeProject(migrateProjectPaths(project) as Project));
      if (state.loadedProject) {
        const updatedLoadedProject = state.projects.find((project) => project.id === state.loadedProject?.id);
        if (updatedLoadedProject) {
          state.loadedProject = hydrateLoadedProject(updatedLoadedProject);
        }
      }
    },
    onProgramsLoading: (state, { payload }) => ({
      ...state,
      programsLoading: payload,
    }),
    onChangeMultiSelect: (state, { payload }) => {
      state.projectSelect.isMultiSelect = payload;
    },
    onSelectProjects: (state, { payload }) => {
      state.projectSelect.selectedProjects = payload;
    },
    onProgramLoadError: (state, { payload }) => {
      state.error = payload;
    },
    onCopiedProject: (state, { payload }) => {
      state.copiedProject = payload;
    },
    onLazyLoading: (state, { payload }) => {
      state.lazyLoading = payload;
    },
    queueProgramReplacement: (state, { payload }: PayloadAction<PendingProgramReplacement>) => {
      state.pendingProgramReplacement = payload;
    },
    clearProgramReplacement: (state) => {
      state.pendingProgramReplacement = null;
    },
    // onUpdatePrograms: (state, { payload }) => {
    //   const compareIdentifier = (p1, p2) => p1.identifier === p2.identifier;
    //   const programs = unionWith(compareIdentifier, payload, state.programs);
    //   state.programs = programs;
    // },
    setDiagnostics: (state, { payload }: PayloadAction<SetDiagnosticsPayload>) => {
      const diagnostics = Array.isArray(payload) ? payload : payload.diagnostics;
      const programCode = Array.isArray(payload) ? state.loadedProject?.program.code ?? null : payload.programCode;
      state.diagnostics = diagnostics;
      state.diagnosticsProjectId = state.loadedProject?.id ?? null;
      state.diagnosticsProgramCode = programCode;
      state.linterDiagnosticsPending = false;
    },
    setLinterDiagnosticsPending: (state, { payload }: PayloadAction<boolean>) => {
      state.linterDiagnosticsPending = payload;
    },
    onSaveLayout: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({ ...project, dashboardLayout: payload }));
    },
    onLoadProject: (state, { payload }) => {
      state.pendingProgramReplacement = null;
      state.sessionSaveSlot = 0;
      state.diagnostics = [];
      state.diagnosticsProjectId = null;
      state.diagnosticsProgramCode = null;
      state.linterDiagnosticsPending = false;
      state.loadedProject = hydrateLoadedProject(migrateProjectPaths(payload) as Draft<Project>);
    },
    onCreateProject: (state, { payload }) => {
      state.pendingProgramReplacement = null;
      state.sessionSaveSlot = 0;
      state.diagnostics = [];
      state.diagnosticsProjectId = null;
      state.diagnosticsProgramCode = null;
      state.linterDiagnosticsPending = false;
      state.loadedProject = hydrateLoadedProject(migrateProjectPaths(payload) as Draft<Project>);
    },
    clearSessionSaveSlot: (state) => {
      state.sessionSaveSlot = 0;
    },
    onUpdatePaths: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({
        ...project,
        paths: payload.map((p: Path) => migratePath(p)),
      }));
    },
    setPathWaypoints: (state, action: PayloadAction<{ pathId: string; waypoints: Waypoint[] }>) => {
      const { pathId, waypoints } = action.payload;
      updateLoadedProject(state, (project) => ({
        ...project,
        paths: project.paths.map((path) => {
          if (path.id !== pathId) return path;
          const segmentSpeeds = syncSegmentSpeedsAfterWaypointEdit(path.segmentSpeeds, path.waypoints, waypoints);
          return { ...path, waypoints, segmentSpeeds };
        }),
      }));
    },
    setPathSegmentSpeed: (state, action: PayloadAction<{ pathId: string; segmentIndex: number; speed: number }>) => {
      const { pathId, segmentIndex, speed } = action.payload;
      const clamped = clampSegmentSpeed(speed);
      updateLoadedProject(state, (project) => ({
        ...project,
        paths: project.paths.map((path) => {
          if (path.id !== pathId) return path;
          const next = syncSegmentSpeedsLen(path.segmentSpeeds, path.waypoints.length);
          if (segmentIndex < 0 || segmentIndex >= next.length) return path;
          next[segmentIndex] = clamped;
          return { ...path, segmentSpeeds: next };
        }),
      }));
    },
    setPathGeometry: (state, action: PayloadAction<{ pathId: string; waypoints: Waypoint[]; segmentSpeeds: number[] }>) => {
      const { pathId, waypoints, segmentSpeeds } = action.payload;
      updateLoadedProject(state, (project) => ({
        ...project,
        paths: project.paths.map((path) =>
          path.id === pathId ? { ...path, waypoints, segmentSpeeds: syncSegmentSpeedsLen(segmentSpeeds, waypoints.length) } : path
        ),
      }));
    },
    rotatePathBuilderField90: (state, action: PayloadAction<{ direction: 'cw' | 'ccw' }>) => {
      const { direction } = action.payload;
      const delta = direction === 'cw' ? 90 : -90;

      updateLoadedProject(state, (project) => {
        const prev = project.pathBuilderFieldRotation ?? 0;
        const selection = normalizeFieldSelection(project.selectedField);
        const baseGeometry = getFieldGeometry(selection);
        const isQuarterTurn = prev % 180 !== 0;
        const currentFieldWidth = isQuarterTurn ? baseGeometry.heightCm : baseGeometry.widthCm;
        const currentFieldHeight = isQuarterTurn ? baseGeometry.widthCm : baseGeometry.heightCm;
        const pathBuilderFieldRotation = (((prev + delta) % 360) + 360) % 360;

        return {
          ...project,
          pathBuilderFieldRotation,
          paths: project.paths.map((path) => ({
            ...path,
            waypoints: path.waypoints.map((wp) => rotateWaypoint90InField(wp, currentFieldWidth, currentFieldHeight, direction)),
          })),
        };
      });
    },
    togglePathVisibility: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      updateLoadedProject(state, (project) => ({
        ...project,
        paths: project.paths.map((path) => (path.id === id ? { ...path, isVisible: !path.isVisible } : path)),
      }));
    },

    deletePath: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      updateLoadedProject(state, (project) => {
        const updatedPaths = project.paths.filter((path) => path.id !== id);
        return {
          ...project,
          paths: updatedPaths,
          selectedPathId: project.selectedPathId === id ? (updatedPaths.length > 0 ? updatedPaths[0].id : '') : project.selectedPathId,
        };
      });
    },
    onUpdateSelectedPathId: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({ ...project, selectedPathId: payload }));
    },
    onUpdateSelectedField: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({ ...project, selectedField: payload }));
    },
    setPathPreviewMode: (state, { payload }: PayloadAction<PathPreviewMode>) => {
      updateLoadedProject(state, (project) => ({ ...project, pathPreviewMode: payload }));
    },
    setCommandManagementEnabled: (state, { payload }: PayloadAction<boolean>) => {
      updateLoadedProject(state, (project) => ({ ...project, commandManagementEnabled: payload }));
    },
    setLeftDrawerContentWidth: (state, { payload }: PayloadAction<{ context: 'codeEditor' | 'dashboard' | 'pathBuilder'; width: number }>) => {
      state.leftDrawerWidths = {
        ...(state.leftDrawerWidths || {}),
        [payload.context]: payload.width,
      };
    },
    setBottomDrawerHeight: (state, { payload }: PayloadAction<number>) => {
      state.bottomDrawerHeight = payload;
    },
    onUpdateProgram: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({
        ...project,
        program: { ...project.program, code: payload },
      }));
    },
    onAddProject: (state, { payload }) => {
      state.projects = [normalizeProject(migrateProjectPaths(payload) as Draft<Project>), ...state.projects];
    },
    onReplaceProject: (state, { payload }) => {
      const normalizedProject = normalizeProject(migrateProjectPaths(payload) as Draft<Project>);
      state.projects = state.projects.map((project) => (project.id === normalizedProject.id ? normalizedProject : project));
    },
    onUpdateLastModified: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({ ...project, lastModified: payload }));
    },
    onUpdateShareCode: (state, { payload }) => {
      if (state.loadedProject === null) return;

      state.projects = state.projects.map((project) => (project.id === payload.id ? payload : project));
    },
    onReadOnly: (state, { payload }) => {
      if (state.loadedProject === null) return;

      state.loadedProject = { ...state.loadedProject, readOnly: payload };
    },
    onSelectEditorTab: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({
        ...project,
        selectedTabs: { ...project.selectedTabs, editorTab: payload },
      }));
    },
    onOpenDrawer: (state, { payload }) => {
      state.openDrawer = payload;
    },
    setSelectedEditorDrawerTab: (state, { payload }: PayloadAction<string | null>) => {
      updateLoadedProject(state, (project) => ({ ...project, selectedEditorDrawerTab: payload }));
    },
    setBuildSubTab: (state, { payload }: PayloadAction<BuildSubTab>) => {
      updateLoadedProject(state, (project) => ({ ...project, buildSubTab: payload }));
    },
    onCloseDrawer: (state, { payload }) => {
      if (state.openDrawer === payload) {
        state.openDrawer = null;
      }
    },
    onSelectDashboardTab: (state, { payload }) => {
      updateLoadedProject(state, (project) => ({
        ...project,
        selectedTabs: { ...project.selectedTabs, dashboardTab: payload },
      }));
    },
    setSelectedSaveSlot: (state, { payload }) => {
      state.sessionSaveSlot = getValidSaveSlotNumber(payload) ?? 0;
    },
    // NEW COLLABORATION ACTIONS
    onSetAccessLevel: (state, { payload }: PayloadAction<{ projectId: string; level: 'collaborator' | 'viewer' | 'local' }>) => {
      const { projectId, level } = payload;
      state.projects = state.projects.map((project) =>
        project.id === projectId ? { ...project, accessLevel: level, readOnly: level === 'viewer' } : project
      );
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, accessLevel: level, readOnly: level === 'viewer' };
      }
    },
    onSetCollaborationState: (state, { payload }: PayloadAction<{ projectId: string; isConnected: boolean; collaborators: CollaboratorInfo[] }>) => {
      const { projectId, isConnected, collaborators } = payload;
      state.projects = state.projects.map((project) =>
        project.id === projectId ? { ...project, isCollaborative: isConnected, collaborators } : project
      );
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, isCollaborative: isConnected, collaborators };
      }
    },
    onYjsConnected: (state, { payload }: PayloadAction<string>) => {
      const projectId = payload;
      state.projects = state.projects.map((project) => (project.id === projectId ? { ...project, isCollaborative: true } : project));
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, isCollaborative: true };
      }
    },
    onYjsDisconnected: (state, { payload }: PayloadAction<string>) => {
      const projectId = payload;
      state.projects = state.projects.map((project) =>
        project.id === projectId ? { ...project, isCollaborative: false, collaborators: [] } : project
      );
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, isCollaborative: false, collaborators: [] };
      }
    },
    onSetProjectPin: (state, { payload }: PayloadAction<{ projectId: string; pin: string }>) => {
      const { projectId, pin } = payload;
      state.projects = state.projects.map((project) => (project.id === projectId ? { ...project, pin } : project));
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, pin };
      }
    },
    onSetProjectShareCode: (state, { payload }: PayloadAction<{ projectId: string; shareCode: string; pin?: string } & ProjectVersionFields>) => {
      const { projectId, shareCode, pin } = payload;
      const metadata = projectApiMetadata(payload);
      state.projects = state.projects.map((project) =>
        project.id === projectId ? { ...project, shareCode, ...(pin && { pin }), ...metadata } : project
      );
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, shareCode, ...(pin && { pin }), ...metadata };
      }
    },
    onClearProjectShareCode: (state, { payload }: PayloadAction<{ projectId: string }>) => {
      const { projectId } = payload;
      const localProjectShareCodeState = {
        shareCode: undefined,
        pin: undefined,
        accessLevel: 'local' as const,
        readOnly: false,
        isCollaborative: false,
        collaborators: [],
      };
      state.projects = state.projects.map((project) => (project.id === projectId ? { ...project, ...localProjectShareCodeState } : project));
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = {
          ...state.loadedProject,
          ...localProjectShareCodeState,
        };
      }
    },
    onUpdateProjectApiMetadata: (state, { payload }: PayloadAction<{ projectId: string } & ProjectVersionFields>) => {
      const { projectId } = payload;
      const metadata = projectApiMetadata(payload);
      state.projects = state.projects.map((project) => (project.id === projectId ? { ...project, ...metadata } : project));
      if (state.loadedProject && state.loadedProject.id === projectId) {
        state.loadedProject = { ...state.loadedProject, ...metadata };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(robotSlice.actions.onDisconnect, (state) => {
      state.programState = RobotActions.PROGRAM.STOPPED;
      state.error = null;
      state.programDeployProgress = null;
      state.activeProgramActionSlotNumber = null;
      state.activeRunningProgramDisplayName = null;
      state.activeRunningProgramSlotNumber = null;
    });
  },
});

export const selectProgramState = (state: RootState) => {
  const { drive, programState, error, loadedProject, activeProgramActionSlotNumber } = state.project;
  return {
    drive,
    programState,
    runtimeProgramState: state.robot.runtimeProgramState,
    error,
    program: loadedProject?.program?.code || '',
    activeProgramActionSlotNumber,
  };
};

export const selectPendingProgramReplacementForLoadedProject = (state: RootState) => {
  const { pendingProgramReplacement, loadedProject } = state.project;
  if (!pendingProgramReplacement || !loadedProject?.id) return null;
  return pendingProgramReplacement.projectId === loadedProject.id ? pendingProgramReplacement : null;
};

export const selectSessionSaveSlot = (state: RootState) => state.project.sessionSaveSlot;

export const selectValidSessionSaveSlotNumber = (state: RootState) => getValidSaveSlotNumber(state.project.sessionSaveSlot);

export const selectActiveProgramActionSlotNumber = (state: RootState) => state.project.activeProgramActionSlotNumber;

export const selectActiveRunningProgramDisplayName = (state: RootState) => state.project.activeRunningProgramDisplayName;

export const selectActiveRunningProgramSlotNumber = (state: RootState) => state.project.activeRunningProgramSlotNumber;

export const {
  onUpdateProgramState,
  setActiveRunningProgramNotificationContext,
  onUpdateProgramDeployProgress,
  onUpdateProgramDrive,
  onAddAndUpsert,
  queueProgramReplacement,
  clearProgramReplacement,
  // addProgramSuceeded,
  addProgramFailed,
  onDeleteProject,
  onDeleteFromServer,
  onSetSortOrder,
  onProgramsLoading,
  onChangeMultiSelect,
  onSelectProjects,
  onProgramLoadError,
  onCopiedProject,
  onCloseDrawer,
  onOpenDrawer,
  setSelectedEditorDrawerTab,
  setBuildSubTab,
  onLazyLoading,
  // onUpdatePrograms,
  onStopProgram,
  onUpdateShareCode,
  setDiagnostics,
  onUpdateProgram,
  onCreateProject,
  onAddProject,
  onReplaceProject,
  onUpdateProjectApiMetadata,
  onUpdateProjects,
  toggleDashboardOpenDrawer,
  setDashboardOpenDrawer,
  toggleEditorOpenDrawer,
  togglePathBuilderOpenDrawer,
  setPathBuilderOpenDrawer,
  toggleBottomBarOpenDrawer,
  setCommandManagementEnabled,
  onAddConsoleDisplay,
  onClearConsoleDisplay,
  onReadOnly,
} = projectSlice.actions;

export default projectSlice.reducer;
